import React, { useState } from "react";
import { Button, Modal, Alert } from "@mui/material";
import { Cpu, Edit2 } from "react-feather";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import LoaderWrapper from "../../../../components/Loaders/LoaderWrapper";
import PrivateHeader from "../../../../components/PrivateHeader";
import ErrorBlockWithPermissions from "../../../../components/UI/ErrorBlockWithPermissions";
import SecondaryButton from "../../../../components/UI/SecondaryButton";
import { ENGAGEMENT_TYPES, JOB_TYPES } from "../../../../storage/constants";
import useLocationDetailV2 from "../../../../swr/hooks/Locations/useLocationDetailV2";
import EngagementTypeSelect from "../../reports/reuseableComponents/EngagementTypeSelect";
import EditLocationDetails from "./EditLocationDetails";
import ToggleManagerDeploy from "./ToggleManagerDeploy";
import QrCodeModal from "../QrCodeModal";
import HeroBlocks from "./HeroBlocks";
import LocationProgress from "./LocationProgress";
import TeamMemberTable from "./TeamMemberTable";
import ManagersDataTable from "./ManagersDataTable";
import SubmissionTable from "./SubmissionTable";
import BreakdownChart from "./BreakdownChart";
import useGlobalStore from "src/store/useGlobalStore";

const TopWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 12px;
  }
`;

const QrBtn = styled(Button)`
  color: black;
  height: 32px;
  padding: 0px 6px;
  border-radius: 6px;
  font-weight: 500;
`;

const HeaderBtnsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const Spacer = styled.div`
  height: 16px;
`;

const LocationDetail = ({ match }) => {
  const { id: locationId } = match.params;
  const { data, isLoading, isError, mutate } = useLocationDetailV2(locationId);
  const setSnackbarText = useGlobalStore((state) => state.setSnackbarText);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const config = useGlobalStore((state) => state.config);
  const { job } = config.user;
  const { read_only: readOnly } = config?.user;
  const isAdmin = job === JOB_TYPES.ADMIN;
  const isManager = job === JOB_TYPES.SUPERVISOR;
  const [engagementType, setEngagementType] = useState(ENGAGEMENT_TYPES.ALL);
  const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);

  if (isLoading) {
    return (
      <>
        <PrivateHeader header="Location" secondary={true} />
        <LoaderWrapper text="Location Details" />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <PrivateHeader header="Location" secondary={true} />
        <ErrorBlockWithPermissions errorObj={isError} />
      </>
    );
  }

  const { submissions } = data;

  return (
    <>
      <Helmet title="Spiffy | Location Detail" />
      <PrivateHeader header="Location" secondary={data?.location?.name}>
        <HeaderBtnsContainer>
          <QrBtn
            size="small"
            color="secondary"
            variant="outlined"
            onClick={() => setQrCodeModalOpen(true)}
            startIcon={<Cpu color="black" height={14} width={14} />}
          >
            Generate QR Code
          </QrBtn>
          <SecondaryButton
            handleClick={() => setEditModalOpen(true)}
            title="Edit Location"
            Icon={Edit2}
            disabled={readOnly || !isAdmin}
          />
        </HeaderBtnsContainer>
      </PrivateHeader>
      <TopWrapper>
        <EngagementTypeSelect
          engagementType={engagementType}
          setEngagementType={setEngagementType}
        />
        <ToggleManagerDeploy
          canDeploy={data?.location?.managers_can_deploy_content}
          mutate={mutate}
          readOnly={readOnly}
          id={locationId}
          isManager={isManager}
          setSnackbarText={setSnackbarText}
        />
      </TopWrapper>
      <div style={{ height: 12 }} />
      <HeroBlocks data={data} engagementType={engagementType} />
      <LocationProgress locationId={locationId} />
      <Spacer />
      <TeamMemberTable
        teamMembers={data.team_members}
        locationName={data.location.name}
        locationId={locationId}
        teamMemberClickable
        data={data}
        engagementType={engagementType}
      />
      <Spacer />
      <ManagersDataTable
        readOnly={false}
        isAdmin
        data={data}
        mutate={mutate}
        setSnackbarText={setSnackbarText}
      />
      <Spacer />
      {submissions?.length > 10 ? (
        <>
          <SubmissionTable
            location={data.location.name}
            submissions={submissions}
            engagementType={engagementType}
          />
          <Spacer />
          <BreakdownChart
            engagementType={engagementType}
            submissions={submissions}
            teamMembers={data.team_members.length}
          />
        </>
      ) : (
        <Alert severity="info" sx={{ mt: 4 }}>
          Additional analytics will be available once your team reaches 10
          submissions. Invite more team members or deploy some modules to boost
          engagement!
        </Alert>
      )}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        aria-labelledby="Edit This Location"
        aria-describedby="Modal To Edit This Location"
      >
        <div>
          <EditLocationDetails
            readOnly={readOnly}
            isAdmin={isAdmin}
            data={data}
            setSnackbarText={setSnackbarText}
            handleClose={() => {
              setEditModalOpen(false);
              mutate();
            }}
          />
        </div>
      </Modal>
      <Modal open={qrCodeModalOpen} onClose={() => setQrCodeModalOpen(false)}>
        <div>
          <QrCodeModal
            locationName={data?.location?.name}
            locationId={locationId}
            teamId={data?.location?.company?.id}
            teamName={data?.location?.company?.name}
          />
        </div>
      </Modal>
    </>
  );
};

export default LocationDetail;
